import { createElement } from 'react';
import { ChakraProvider } from '@chakra-ui/provider';
import { Provider } from 'jotai';
import { extendTheme, withDefaultVariant, withDefaultColorScheme } from '@chakra-ui/react';
import '@laliga-cdac/fonts/coreSans.css';

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

var styles = {
  global: {
    body: {
      fontFamily: 'body'
    },
    '::-webkit-scrollbar': {
      appearance: 'none'
    },
    '::-webkit-scrollbar:vertical': {
      width: '10px'
    },
    '::-webkit-scrollbar:horizontal': {
      height: '10px'
    },
    '::-webkit-scrollbar-thumb': {
      backgroundColor: '#797979',
      borderRadius: '20px',
      border: '2px solid #f1f2f3'
    },
    '::-webkit-scrollbar-track': {
      borderRadius: '10px'
    }
  }
};
var colors = {
  corporateWhite: '#FFFFFF',
  corporateCyan: {
    50: '#87EEFF',
    100: '#6DE5F8',
    200: '#53DDF2',
    300: '#39D4EB',
    400: '#1FCCE5',
    500: '#05C3DE',
    600: '#05AAC1',
    700: '#0491A5',
    800: '#047788',
    900: '#035E6B'
  },
  corporateRed: {
    50: '#FFA3B4',
    100: '#F68298',
    200: '#ED627C',
    300: '#E44160',
    400: '#DB2144',
    500: '#D20028',
    600: '#AF0022',
    700: '#8D001B',
    800: '#6A0014',
    900: '#47000E'
  },
  corporatePurple: {
    50: '#D1BFFF',
    100: '#BBA8ED',
    200: '#A692DA',
    300: '#907BC8',
    400: '#7B65B5',
    500: '#654EA3',
    600: '#55408E',
    700: '#463379',
    800: '#362564',
    900: '#26174F'
  },
  corporateBlue: {
    50: '#9ED8FF',
    100: '#7EC5F4',
    200: '#5FB1E9',
    300: '#3F9EDE',
    400: '#208AD3',
    500: '#0077C8',
    600: '#0064A9',
    700: '#005289',
    800: '#003F6A',
    900: '#002C4A'
  },
  corporateGreen: {
    50: '#9CFFE6',
    100: '#7DF0D3',
    200: '#5EE1C0',
    300: '#3ED1AE',
    400: '#1FC29B',
    500: '#00B388',
    600: '#009773',
    700: '#007C5F',
    800: '#00614A',
    900: '#004535'
  },
  corporateBlack: {
    50: '#CFE0ED',
    100: '#ADBBC6',
    200: '#8B969F',
    300: '#697278',
    400: '#474D51',
    500: '#25282A',
    600: '#1C1E20',
    700: '#131415',
    800: '#090A0B',
    900: '#000000'
  },
  corporateYellow: {
    50: '#F6E7B4',
    100: '#F6DF90',
    200: '#F6D76C',
    300: '#F6CE48',
    400: '#F6C624',
    500: '#F6BE00',
    600: '#C59900',
    700: '#957300',
    800: '#644E00',
    900: '#332800'
  },
  corporateOrange: {
    50: '#FFD7C9',
    100: '#FFC1AC',
    200: '#FFAB8F',
    300: '#FF9673',
    400: '#FF8056',
    500: '#FF6A39',
    600: '#CD552E',
    700: '#9B4023',
    800: '#682B17',
    900: '#36160C'
  },
  corporateGrey: {
    50: '#F8F8F9',
    100: '#E5E6E8',
    200: '#D3D4D8',
    300: '#C0C3C7',
    400: '#AEB1B7',
    500: '#9B9FA6',
    600: '#85898F',
    700: '#6F7378',
    800: '#585D61',
    900: '#42474A'
  }
};
var typography = {
  fonts: {
    body: 'coreSans',
    heading: 'coreSans'
  }
};

var foundations = /*#__PURE__*/_extends({
  colors: colors
}, typography);

var formSizes = {
  xs: {
    field: {
      height: '24px',
      borderRadius: '4px',
      fontSize: '10px',
      padding: '3px 8px'
    }
  },
  sm: {
    field: {
      height: '28px',
      borderRadius: '4px',
      fontSize: '12px',
      padding: '5px 8px'
    }
  },
  md: {
    field: {
      borderRadius: '4px',
      height: '34px',
      fontSize: '14px',
      padding: '8px 8px 8px 16p'
    }
  },
  lg: {
    field: {
      height: '38px',
      borderRadius: '6px',
      fontSize: '15px',
      padding: '10px 8px 10px 16px'
    }
  },
  xl: {
    field: {
      height: '42px',
      borderRadius: '6px',
      fontSize: '16px',
      padding: '12px 8px 12px 16px'
    }
  },
  '2xl': {
    field: {
      height: '46px',
      borderRadius: 'none',
      fontSize: '18px',
      padding: '14px 8px 14px 16px'
    }
  }
};
var Input = {
  sizes: /*#__PURE__*/_extends({}, formSizes),
  variants: {
    laliga: function laliga(props) {
      var darkMode = props.darkMode,
          transparentMode = props.transparentMode;
      var bgDarkMode = darkMode ? 'corporateGrey.700' : 'corporateGrey.50';
      var bgDarkModeHover = darkMode ? 'corporateGrey.600' : 'corporateGrey.100';
      var bg = transparentMode ? 'transparent' : bgDarkMode;
      var bgHover = transparentMode ? 'transparent' : bgDarkModeHover;
      var textDarkMode = darkMode ? 'corporateGrey.100' : 'corporateGrey.700';
      var colorText = transparentMode ? 'white' : textDarkMode;
      return {
        field: {
          bg: bg,
          borderRadius: '0',
          borderWidth: 'thin',
          borderColor: 'transparent',
          color: colorText,
          _hover: {
            bg: bgHover,
            borderColor: 'transparent'
          },
          _placeholder: {
            color: colorText
          },
          _invalid: {
            borderColor: 'red.500'
          },
          _disabled: {
            pointerEvents: 'none',
            opacity: 0.5
          }
        }
      };
    }
  }
};
var Button = {
  baseStyle: {
    _focus: {
      boxShadow: 'none'
    }
  },
  variants: {
    link: {
      _hover: {
        textDecoration: 'none'
      }
    }
  }
};
var Switch = {
  baseStyle: function baseStyle(props) {
    return {
      _focus: {
        boxShadow: 'none'
      },
      track: {
        bg: 'white',
        border: '2px solid',
        borderColor: props.colorScheme + ".500",
        _checked: {
          bg: props.colorScheme + ".500"
        },
        _focus: {
          boxShadow: 'none'
        }
      },
      thumb: {
        bg: props.colorScheme + ".500",
        _checked: {
          bg: 'white'
        }
      }
    };
  }
};
var Textarea = {
  variants: {
    laliga: function laliga(props) {
      var darkMode = props.darkMode,
          transparentMode = props.transparentMode;
      var bgDarkMode = darkMode ? 'corporateGrey.700' : 'corporateGrey.50';
      var bgDarkModeHover = darkMode ? 'corporateGrey.600' : 'corporateGrey.100';
      var bg = transparentMode ? 'transparent' : bgDarkMode;
      var bgHover = transparentMode ? 'transparent' : bgDarkModeHover;
      var textDarkMode = darkMode ? 'corporateGrey.100' : 'corporateGrey.700';
      var colorText = transparentMode ? 'white' : textDarkMode;
      return {
        bg: bg,
        borderRadius: '0',
        borderWidth: 'thin',
        borderColor: 'transparent',
        color: colorText,
        _hover: {
          bg: bgHover,
          borderColor: 'transparent'
        },
        _placeholder: {
          color: colorText
        },
        _invalid: {
          borderColor: 'red.500'
        },
        _disabled: {
          pointerEvents: 'none',
          opacity: 0.5
        }
      };
    }
  }
};
var components = {
  Input: Input,
  Button: Button,
  Switch: Switch,
  Textarea: Textarea
};

var theme = function theme(overridenTheme) {
  return extendTheme(_extends({
    styles: styles
  }, foundations, {
    components: components,
    config: {
      useSystemColorMode: false
    }
  }, overridenTheme), withDefaultVariant({
    variant: 'laliga',
    components: ['Input', 'Textarea']
  }), withDefaultColorScheme({
    colorScheme: 'corporateBlue',
    components: ['Button', 'Checkbox', 'Switch']
  }));
};

var LaLigaProvider = function LaLigaProvider(props) {
  var children = props.children,
      externalTheme = props.theme;
  return createElement(ChakraProvider, {
    theme: theme(externalTheme)
  }, createElement(Provider, null, children));
};

export { LaLigaProvider, colors, theme };